// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Check from "@ces/XDL/Icons/Check";
import { fontConfig } from "@ces/XDL/PreStyle/typography";
import { xBaseTheme } from "@ces/XDL/types";
import color from "@ces/XDL/Utilities/color";
import CheckboxPresentationPropsDefault from "@insite/mobius/Checkbox/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

export const CheckboxInputSelect = `span[role="checkbox"]`;

export const CheckboxCheckedSelector = `
    &[aria-checked="true"]:not([aria-disabled="true"]),
    &[aria-checked="mixed"]:not([aria-disabled="true"])
`;

export const CheckboxDefaultSelector = `&[aria-checked="false"]:not([aria-disabled="true"])`;
export const CheckboxDisabledSelector = `&[aria-disabled="true"]`;

const defaultStateStyles = css`
    & ${CheckboxInputSelect} {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 13px;
        min-width: 13px;
        width: 13px;
        border-color: ${color("common.ui4")};
        border-radius: 2px;
        background: ${color("common.ui2")};
        &:focus {
            box-shadow: none;
        }
        & + label {
            margin-left: 8px;
        }
    }
`;

const checkedStyle = css`
    & ${CheckboxInputSelect} {
        ${CheckboxCheckedSelector} {
            background: ${color("primary")};
            border-color: ${color("primary")};
            color: ${color("common.ui1")};
        }
    }
`;
const hoverStyle = css`
    &:hover {
        ${CheckboxInputSelect} {
            ${CheckboxCheckedSelector} {
                background: ${color("common.ui8")};
                border-color: ${color("common.ui8")};
            }
            ${CheckboxDefaultSelector} {
                border-color: ${color("common.ui8")};
            }
        }
    }
`;

const disabledStyle = css`
    ${CheckboxInputSelect} {
        ${CheckboxDisabledSelector} {
            background: ${color("common.disabled", 0.33)};
            border: 1px solid ${color("common.border")};
            color: ${color("common.black", 0.33)};
            cursor: not-allowed;
        }
        ${CheckboxDisabledSelector} + label {
            opacity: 0.33;
            cursor: not-allowed;
        }
    }
`;

export default {
    defaultProps: {
        ...CheckboxPresentationPropsDefault,
        mergeCss: true,
        typographyProps: {
            mergeCss: true,
            size: 12,
            weight: fontConfig.weight.regular,
            color: "text.main",
            css: css`
                letter-spacing: 0;
                line-height: 13px;
                user-select: none;
            `,
        },
        iconProps: {
            size: 9,
            src: Check,
            css: css`
                &,
                & > svg {
                    height: 13px;
                }
            `,
        },
        css: css`
            &:not(:disabled) {
                cursor: pointer;
            }
            ${defaultStateStyles}
            ${hoverStyle}
            ${checkedStyle}
            ${disabledStyle}
        `,
    },
    groupDefaultProps: {
        labelProps: {
            variant: "h6",
        },
        errorProps: {
            css: css`
                &:empty {
                    display: none;
                }
            `,
        },
        css: css`
            & > ${TypographyStyle as any} {
                font-size: 16px;
                line-height: 19px;
                font-weight: ${fontConfig.weight.bold};
                &:first-child {
                    margin-bottom: 20px;
                }
                &:last-child {
                    margin-top: 20px;
                }
                &:empty {
                    display: none;
                }
            }
        `,
    },
} as xBaseTheme["checkbox"];
